// src/hooks/useNotifications.tsx
// Orders/notifications, backed by the shared in-memory ordersStore (no IndexedDB).
// Pagination is per-instance (local `page`); the data array is shared across all
// hook instances, so the backend is hit once regardless of how many widgets mount.
import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import type { Notification } from '../types/Notification';
import { MOCK_ORDERS_ENABLED } from '../mocks/config';
import { markOrdersSold } from '../services/orderSync';
import { ordersStore } from '../stores/orderStores';

// Номера строк заказов по id уведомлений: статус меняется ровно у этих заказов.
// До 31.10.2026 из id вырезался телефон, и «продано» ставилось всем заказам покупателя.
const rowIdsOf = (ids: string[]): number[] => {
  const want = new Set(ids);
  return ordersStore.getSnapshot()
    .filter((n) => want.has(n.id))
    .flatMap((n) => n.rowIds ?? []);
};

export function useNotifications(pageSize = 10) {
  const all = useSyncExternalStore(ordersStore.subscribe, ordersStore.getSnapshot);
  const loading = useSyncExternalStore(ordersStore.subscribe, ordersStore.getLoading);
  const [page, setPage] = useState(1);

  // Refetch on mount; concurrent mounts of multiple widgets dedupe to one request.
  useEffect(() => { void ordersStore.refresh(); }, []);

  const sorted = useMemo(
    () => [...all].sort((a, b) => +new Date(b.createdAt) - +new Date(a.createdAt)),
    [all],
  );
  const total = sorted.length;
  const unread = useMemo(
    () => sorted.filter((n) => !n.isRead && !n.isHidden && n.status === 'new').length,
    [sorted],
  );
  // Список колокольчика: новые заказы, кроме убранных «Удалить». Берётся из всех заказов,
  // а не из текущей страницы — иначе новый заказ за десятком обработанных в окно не попадал.
  const bell = useMemo(
    () => sorted.filter((n) => n.status === 'new' && !n.isHidden),
    [sorted],
  );
  const maxPage = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(Math.max(1, page), maxPage);
  const items = useMemo(() => {
    const offset = (safePage - 1) * pageSize;
    return sorted.slice(offset, offset + pageSize);
  }, [sorted, safePage, pageSize]);

  const markRead = useCallback(async (ids: string[]) => {
    ordersStore.markRead(ids ?? []);
  }, []);

  // «Удалить» в колокольчике — только убрать уведомление (слово владельца 31.10.2026).
  // До этого оно ставило заказу «Продан» и списывало склад, хотя заказ мог быть не продан.
  const remove = useCallback(async (ids: string[]) => {
    if (!ids?.length) return;
    ordersStore.hide(ids);
  }, []);

  // Mark a single order as sold. Real mode: backend + refetch; mock: drop locally.
  const markSold = useCallback(async (id: string, _phone?: string) => {
    if (!MOCK_ORDERS_ENABLED) {
      await markOrdersSold(rowIdsOf([id]));
      await ordersStore.refresh();
    } else {
      ordersStore.removeLocal([id]);
    }
  }, []);

  const addNotification = useCallback(async (n: Notification) => {
    ordersStore.prepend(n);
    setPage(1);
  }, []);

  return {
    items, unread, total, bell,
    page: safePage, setPage, pageSize, loading,
    reload: () => ordersStore.refresh(),
    markRead, remove, addNotification, markSold,
  };
}
